/**
 * Translation data
 * 
 * Pure FR/EN translation data moved from apps/frontend/src/i18n.js.
 * This data is framework-agnostic and can be used anywhere.
 * 
 * IMPORTANT: NO React imports here! This is pure data.
 */

import type { Translations, TranslationDictionary, Lang } from './types';

/**
 * French translations.
 */
const fr: Translations = {
  // FolderNavigator toolbar
  newRootFolder: 'Nouveau dossier parent',
  newFolder: 'Nouveau dossier',
  newProject: 'Nouveau projet',
  newWhiteboard: 'Nouveau whiteboard',
  newTextDoc: 'Nouveau document texte',
  save: 'Sauvegarder',
  settings: 'Paramètres',
  
  // Tree
  home: 'Accueil',
  empty: 'Vide',
  folderPlaceholder: 'Nom du dossier',
  projectPlaceholder: 'Nom du projet',
  deleteConfirm: (name: unknown) => `Supprimer "${name}" ?`,
  
  // Context menu
  ctxNewFolder: '📁 Nouveau dossier',
  ctxNewProject: '💡 Nouveau projet',
  ctxNewWhiteboard: 'Nouveau whiteboard',
  ctxNewTextDoc: 'Nouveau document texte',
  ctxRename: '✏️ Renommer',
  ctxDuplicate: '📋 Dupliquer',
  ctxDelete: '🗑️ Supprimer',
  
  // Rename modal
  renameTitle: 'Renommer',
  ok: 'OK',
  cancel: 'Annuler',
  
  // User bar
  user: 'Utilisateur',
  logout: 'Se déconnecter',
  about: 'À propos',
  aboutTitle: 'À propos de cet outil',
  aboutBody: "Cet outil est développé par ESSONE GROUP, maison mère de IPFLUENCER SAS et commercialisant le service CertifAfrica. L'approche idéation utilisée est celle de la méthode C-K qui a été mise au point par MINES PARIS TECH. Cette méthode de création structurée a fait ses preuves dans de nombreux domaines et semble sans limite.",
  aboutSlogan: 'Impactons le monde positivement',
  
  // Members panel
  membersBtn: 'Membres',
  membersTitle: (n: unknown, max: unknown) => `Collaborateurs ${n}${max ? ` / ${max}` : ''}`,
  membersTabMembers: 'Membres',
  membersTabGuests1: 'Invités d\'un projet',
  membersTabGuestsN: 'Invités de plusieurs projets',
  membersTabPending: 'Demandes d\'ajout',
  membersFilter: 'Filtrer par nom…',
  membersInviteBtn: '＋ Inviter des membres',
  membersInviteEmail: 'E-mail ou identifiant',
  membersSend: 'Inviter',
  membersLastSeen: (date: unknown) => `Dernière activité le ${date}`,
  membersNone: 'Aucun membre',
  membersRemove: 'Retirer',
  membersLeave: 'Quitter',
  membersRoleMember: 'Membre',
  membersRoleGuest: 'Invité',
  membersRoleAdmin: 'Administrateur',
  membersInviteOk: '✓ Invitation envoyée',
  membersInviteErr: '✕ Erreur lors de l\'envoi',
  
  // Admin panel
  adminPanel: 'Administration',
  adminUsers: 'Utilisateurs',
  adminInvite: 'Inviter un utilisateur',
  adminInviteEmail: 'E-mail du nouvel utilisateur',
  adminInviteRole: 'Rôle',
  adminRoleUser: 'Utilisateur',
  adminRoleAdmin: 'Administrateur',
  adminSendInvite: 'Envoyer l\'invitation',
  adminInviteOk: '✓ Invitation envoyée',
  adminInviteErr: '✕ Erreur lors de l\'envoi',
  adminActive: 'Actif',
  adminInactive: 'Inactif',
  adminToggleOn: 'Activer',
  adminToggleOff: 'Désactiver',
  adminRemove: 'Retirer',
  adminRemoveConfirm: (name: unknown) => `Retirer "${name}" de l'organisation ?`,
  adminAccessFull: 'Accès complet',
  adminAccessRead: 'Lecture seule',
  adminAccessNone: 'Aucun accès',
  adminSaved: '✓ Modifications enregistrées',
  adminError: '✕ Erreur',
  adminName: 'Nom',
  adminEmail: 'E-mail',
  adminRole: 'Rôle',
  adminStatus: 'Statut',
  adminActions: 'Actions',
  adminNoUsers: 'Aucun utilisateur',
  
  // Settings modal
  settingsTitle: 'Paramètres du compte',
  firstName: 'Prénom',
  lastName: 'Nom',
  organisation: 'Organisation',
  email: 'E-mail',
  saveProfile: 'Enregistrer',
  profileSaved: '✓ Profil mis à jour',
  profileError: '✕ Erreur lors de la mise à jour',
  changePwd: 'Changer le mot de passe',
  oldPwd: 'Mot de passe actuel',
  newPwd: 'Nouveau mot de passe',
  confirmPwd: 'Confirmer le nouveau mot de passe',
  pwdMismatch: '✕ Les mots de passe ne correspondent pas',
  pwdChanged: '✓ Mot de passe modifié',
  pwdError: '✕ Mot de passe actuel incorrect',
  updatePwd: 'Modifier',
  
  // Concept map — title box default
  examinedObject: 'Idée frugale',
  
  // Concept map — section headers
  conceptSpace: 'ESPACE DES CONCEPTS',
  knowledgeSpace: 'ESPACE DES CONNAISSANCES',
  
  // Concept map — context menus
  expand: '▶  Développer',
  collapse: '▼  Réduire',
  addChild: '＋  Ajouter un enfant',
  duplicateNode: '⧉  Dupliquer ce nœud',
  addConcept: '＋  Ajouter un concept',
  addLevel: '＋  Ajouter un niveau',
  deleteNode: '✕  Supprimer ce nœud',
  deleteLevel: '✕  Supprimer ce niveau',
  colorGreen: '🟢  Vert',
  colorYellow: '🟡  Jaune',
  colorRed: '🔴  Rouge',
  colorDefault: '⚪  Blanc (défaut)',
  addNodeImage: '🖼  Ajouter une image',
  removeNodeImage: '🗑  Supprimer l\'image',
  nodeImageTitle: 'Image du concept',
  nodeImageUrlPh: 'Coller une URL d\'image…',
  nodeImageUpload: 'Choisir un fichier',
  nodeImagePreview: 'Aperçu',
  dupKnowledge: '⧉  Dupliquer',
  addExisting: '＋  Connaissance existante',
  addToAcquire: '＋  Connaissance à acquérir',
  deleteKnowledge: '✕  Supprimer',
  
  // Level buttons
  addLevelBtn: '+ Niveau',
  removeLevelBtn: '− Niveau',
  
  // Legend
  legendTitle: 'LÉGENDE',
  legendExisting: 'Connaissances existantes',
  legendToAcquire: 'Connaissances à acquérir',
  legendDesc: 'Description…',
  
  // Bottom legend
  domDesign: 'Modèle dominant :',
  domDesignSub: "propriétés de l'objet",
  conceptsLabel: 'Concepts :',
  conceptsSub: 'explorés ou à explorer',
  knowledgeLabel: 'Connaissances :',
  knowledgeSub: 'utilisées pour créer des concepts ou à acquérir',
  
  // NodeLabel placeholder
  clickLabel: 'cliquer…',
  
  // SpineLabel / KnowledgeCard placeholder
  clickDescribe: 'cliquer pour décrire…',
  clickEdit: 'Cliquer pour éditer…',
  
  // KnowledgeCardEditor
  editCard: "Éditer l'étiquette",
  headerColor: "Couleur de l'entête",
  borderStyle: 'Style de contour',
  borderSolid: 'Continu (existante)',
  borderDashed: 'Discontinu (à acquérir)',
  titleLabel: 'Titre',
  titlePlaceholder: 'Saisir un titre…',
  textLabel: '📝 Texte',
  textPlaceholder: 'Saisir un texte…',
  imageLabel: '🖼 Image',
  imageUrlPh: "URL de l'image (https://…)",
  orLabel: 'ou',
  fromComputer: '📂 Depuis l\'ordinateur',
  removeImage: '✕',
  linkLabel: '🔗 Lien (PDF, PPT, Word, article…)',
  linkPh: 'https://… ou laisser vide',
  uploadDoc: '📎 Uploader un document',
  docUploaded: '✓ Document uploadé',
  saveBtn: 'Enregistrer',
  cancelBtn: 'Annuler',
  
  // Export
  exportBtn: 'Exporter',
  exportSvg: 'SVG',
  exportSvgHint: 'Vectoriel, haute qualité',
  exportPng: 'PNG',
  exportPngHint: 'Image haute résolution (×3)',
  exportPdf: 'PDF',
  exportPdfHint: 'Via impression navigateur',
  exportWorkbook: 'Exporter le workbook',
  exportWorkbookHint: 'Tous vos dossiers et projets en un .json',
  importWorkbook: 'Importer un workbook',
  importWorkbookLabel: 'Choisissez un fichier .json exporté précédemment. Les dossiers et projets existants seront fusionnés.',

  // Share
  shareBtn: 'Partager',
  shareTitle: 'Partager le projet',
  shareWith: 'Destinataire',
  sharePlaceholder: 'Chercher un membre ou saisir un e-mail…',
  shareFormat: 'Format d\'envoi',
  shareAccessOnly: 'Accès au projet uniquement (sans fichier)',
  shareSending: 'Envoi en cours…',
  shareSuccess: '✓ Envoyé avec succès',
  shareError: '✕ Erreur lors de l\'envoi',
  shareSendBtn: 'Envoyer',
  
  // Notifications
  notifications: 'Notifications',
  noNotifications: 'Aucune notification',
  projectSharedBy: (from: unknown) => `Projet partagé par ${from}`,
  importToHome: '⬇ Importer dans Accueil',
  importing: 'Import en cours…',
  importNotifSuccess: '✓ Importé dans Accueil',
  importNotifError: '✕ Échec de l\'import',
  markRead: 'Marquer comme lu',
  
  // Import from link
  importBtn: 'Importer un projet',
  importTitle: 'Importer un projet',
  importLinkLabel: 'Lien ou identifiant du projet',
  importLinkPh: 'Collez le lien reçu par e-mail…',
  importSuccess: '✓ Projet importé dans Accueil',
  importError: '✕ Erreur lors de l\'import',

  // Auth
  authLogin: 'Se connecter',
  authSignup: 'Créer un compte',
  authEmail: 'E-mail',
  authPassword: 'Mot de passe',
  authNickname: 'Pseudo (optionnel)',
  authLoggingIn: 'Connexion…',
  authSigningUp: 'Inscription…',
  authNoAccount: 'Pas encore de compte ?',
  authHasAccount: 'Déjà un compte ?',
  authCreateAccount: 'Créer un compte',
  authLoginLink: 'Se connecter',
  authForgotPwd: 'Mot de passe oublié ?',
  authServerError: 'Réponse invalide du serveur',
  authBadCredentials: 'Identifiants incorrects',
  authVerifyTitle: 'Vérifiez votre e-mail',
  authVerifyMsg: 'Un code de vérification a été envoyé à',
  authVerifyCode: 'Code de vérification (6 chiffres)',
  authVerifying: 'Vérification…',
  authVerifyBtn: 'Vérifier',
  authResendCode: 'Renvoyer le code',
  authSignupSuccess: 'Compte créé ! Vous pouvez maintenant vous connecter.',
  authSignupSuccessVerify: 'Compte créé ! Vérifiez votre e-mail pour activer votre compte.',
  authResetTitle: 'Réinitialiser le mot de passe',
  authResetMsg: 'Entrez votre e-mail pour recevoir un lien de réinitialisation.',
  authResetSending: 'Envoi…',
  authResetSend: 'Envoyer le lien',
  authResetSent: 'Si cet e-mail existe, un lien de réinitialisation a été envoyé.',
  authBackToLogin: 'Retour à la connexion',
  authWelcome: 'Intelligence Collective',
  authWelcomeSub: 'Connectez-vous pour continuer',

  // Brand tagline (free-tier floating badge)
  tagline: "Mobilisons l'intelligence collective",

  // Upgrade CTA + modal (free tier only)
  upgradeBtn: 'Passer à la version payante',
  upgradeTitle: 'Passer à la version payante',
  upgradeSubtitle: "Débloquez la collaboration, le stockage serveur, le partage de projets et les comptes utilisateurs. Choisissez le plan adapté à votre structure.",
  upgradePopular: 'Populaire',
  upgradeClose: 'Fermer',
  upgradeFooter: 'Tous les plans incluent : cartes mentales C-K, whiteboards, documents texte, export PDF.',
  upgradePerUserYear: '/ an / utilisateur',
  upgradeCustomQuote: 'Sur devis',
  upgradeSeatsUnlimited: 'Sièges illimités',
  upgradeSeats1to2: '1 à 2 sièges',
  upgradeSeats3to10: '3 à 10 sièges',

  tierSchoolName: 'School & ONG',
  tierSchoolDesc: 'Écoles, universités, associations. Sans limite de collaborateurs.',
  tierIndepName: 'Indépendant',
  tierIndepDesc: 'Freelances et petites équipes. Collaboration légère.',
  tierTpeName: 'TPE / PME',
  tierTpeDesc: "Équipes organisées en entreprise. Tarif dégressif.",
  tierLargeName: 'Grandes Entreprises',
  tierLargeDesc: 'SSO, déploiement dédié, support prioritaire, SLA.',

  ctaContactUs: 'Nous contacter',
  ctaSubscribe: 'Souscrire',
  ctaRequestQuote: 'Demander un devis',
};

/**
 * English translations.
 */
const en: Translations = {
  // FolderNavigator toolbar
  newRootFolder: 'New root folder',
  newFolder: 'New folder',
  newProject: 'New project',
  newWhiteboard: 'New whiteboard',
  newTextDoc: 'New text document',
  save: 'Save',
  settings: 'Settings',
  
  // Tree
  home: 'Home',
  empty: 'Empty',
  folderPlaceholder: 'Folder name',
  projectPlaceholder: 'Project name',
  deleteConfirm: (name: unknown) => `Delete "${name}"?`,
  
  // Context menu
  ctxNewFolder: '📁 New folder',
  ctxNewProject: '💡 New project',
  ctxNewWhiteboard: 'New whiteboard',
  ctxNewTextDoc: 'New text document',
  ctxRename: '✏️ Rename',
  ctxDuplicate: '📋 Duplicate',
  ctxDelete: '🗑️ Delete',
  
  // Rename modal
  renameTitle: 'Rename',
  ok: 'OK',
  cancel: 'Cancel',
  
  // User bar
  user: 'User',
  logout: 'Log out',
  about: 'About',
  aboutTitle: 'About this tool',
  aboutBody: "This tool is developed by ESSONE GROUP, parent company of IPFLUENCER SAS and the company behind the CertifAfrica service. The ideation approach used is the C-K method, developed by MINES PARIS TECH. This structured creation method has proven itself in many fields and seems to have no limits.",
  aboutSlogan: 'Let\'s impact the world positively',
  
  // Members panel
  membersBtn: 'Members',
  membersTitle: (n: unknown, max: unknown) => `Collaborators ${n}${max ? ` / ${max}` : ''}`,
  membersTabMembers: 'Members',
  membersTabGuests1: 'Single-project guests',
  membersTabGuestsN: 'Multi-project guests',
  membersTabPending: 'Join requests',
  membersFilter: 'Filter by name…',
  membersInviteBtn: '＋ Invite members',
  membersInviteEmail: 'E-mail or user ID',
  membersSend: 'Invite',
  membersLastSeen: (date: unknown) => `Last active ${date}`,
  membersNone: 'No members',
  membersRemove: 'Remove',
  membersLeave: 'Leave',
  membersRoleMember: 'Member',
  membersRoleGuest: 'Guest',
  membersRoleAdmin: 'Administrator',
  membersInviteOk: '✓ Invitation sent',
  membersInviteErr: '✕ Error sending invitation',
  
  // Admin panel
  adminPanel: 'Administration',
  adminUsers: 'Users',
  adminInvite: 'Invite a user',
  adminInviteEmail: 'New user e-mail',
  adminInviteRole: 'Role',
  adminRoleUser: 'User',
  adminRoleAdmin: 'Administrator',
  adminSendInvite: 'Send invitation',
  adminInviteOk: '✓ Invitation sent',
  adminInviteErr: '✕ Error sending invitation',
  adminActive: 'Active',
  adminInactive: 'Inactive',
  adminToggleOn: 'Enable',
  adminToggleOff: 'Disable',
  adminRemove: 'Remove',
  adminRemoveConfirm: (name: unknown) => `Remove "${name}" from organisation?`,
  adminAccessFull: 'Full access',
  adminAccessRead: 'Read only',
  adminAccessNone: 'No access',
  adminSaved: '✓ Changes saved',
  adminError: '✕ Error',
  adminName: 'Name',
  adminEmail: 'E-mail',
  adminRole: 'Role',
  adminStatus: 'Status',
  adminActions: 'Actions',
  adminNoUsers: 'No users',
  
  // Settings modal
  settingsTitle: 'Account settings',
  firstName: 'First name',
  lastName: 'Last name',
  organisation: 'Organisation',
  email: 'E-mail',
  saveProfile: 'Save',
  profileSaved: '✓ Profile updated',
  profileError: '✕ Update failed',
  changePwd: 'Change password',
  oldPwd: 'Current password',
  newPwd: 'New password',
  confirmPwd: 'Confirm new password',
  pwdMismatch: '✕ Passwords do not match',
  pwdChanged: '✓ Password changed',
  pwdError: '✕ Current password incorrect',
  updatePwd: 'Update',
  
  // Concept map — title box default
  examinedObject: 'Frugal idea',
  
  // Concept map — section headers
  conceptSpace: 'CONCEPT SPACE',
  knowledgeSpace: 'KNOWLEDGE SPACE',
  
  // Concept map — context menus
  expand: '▶  Expand',
  collapse: '▼  Collapse',
  addChild: '＋  Add child',
  duplicateNode: '⧉  Duplicate this node',
  addConcept: '＋  Add concept',
  addLevel: '＋  Add level',
  deleteNode: '✕  Delete this node',
  deleteLevel: '✕  Delete this level',
  colorGreen: '🟢  Green',
  colorYellow: '🟡  Yellow',
  colorRed: '🔴  Red',
  colorDefault: '⚪  White (default)',
  addNodeImage: '🖼  Add an image',
  removeNodeImage: '🗑  Remove image',
  nodeImageTitle: 'Concept image',
  nodeImageUrlPh: 'Paste an image URL…',
  nodeImageUpload: 'Choose a file',
  nodeImagePreview: 'Preview',
  dupKnowledge: '⧉  Duplicate',
  addExisting: '＋  Existing knowledge',
  addToAcquire: '＋  Knowledge to acquire',
  deleteKnowledge: '✕  Delete',
  
  // Level buttons
  addLevelBtn: '+ Level',
  removeLevelBtn: '− Level',
  
  // Legend
  legendTitle: 'LEGEND',
  legendExisting: 'Existing knowledge',
  legendToAcquire: 'Knowledge to acquire',
  legendDesc: 'Description…',
  
  // Bottom legend
  domDesign: 'Dominant Design:',
  domDesignSub: 'object properties',
  conceptsLabel: 'Concepts:',
  conceptsSub: 'explored or to explore',
  knowledgeLabel: 'Knowledge:',
  knowledgeSub: 'used to create concepts or to acquire',
  
  // NodeLabel placeholder
  clickLabel: 'click…',
  
  // SpineLabel / KnowledgeCard placeholder
  clickDescribe: 'click to describe…',
  clickEdit: 'Click to edit…',
  
  // KnowledgeCardEditor
  editCard: 'Edit card',
  headerColor: 'Header colour',
  borderStyle: 'Border style',
  borderSolid: 'Solid (existing)',
  borderDashed: 'Dashed (to acquire)',
  titleLabel: 'Title',
  titlePlaceholder: 'Enter a title…',
  textLabel: '📝 Text',
  textPlaceholder: 'Enter text…',
  imageLabel: '🖼 Image',
  imageUrlPh: 'Image URL (https://…)',
  orLabel: 'or',
  fromComputer: '📂 From computer',
  removeImage: '✕',
  linkLabel: '🔗 Link (PDF, PPT, Word, article…)',
  linkPh: 'https://… or leave empty',
  uploadDoc: '📎 Upload document',
  docUploaded: '✓ Document uploaded',
  saveBtn: 'Save',
  cancelBtn: 'Cancel',
  
  // Export
  exportBtn: 'Export',
  exportSvg: 'SVG',
  exportSvgHint: 'Vector, best quality',
  exportPng: 'PNG',
  exportPngHint: 'High-res image (×3)',
  exportPdf: 'PDF',
  exportPdfHint: 'Via browser print',
  exportWorkbook: 'Export workbook',
  exportWorkbookHint: 'All your folders and projects in one .json',
  importWorkbook: 'Import a workbook',
  importWorkbookLabel: 'Pick a .json file exported previously. Existing folders and projects will be merged.',

  // Share
  shareBtn: 'Share',
  shareTitle: 'Share project',
  shareWith: 'Recipient',
  sharePlaceholder: 'Search a member or enter an e-mail…',
  shareFormat: 'Send format',
  shareAccessOnly: 'Project access only (no file)',
  shareSending: 'Sending…',
  shareSuccess: '✓ Sent successfully',
  shareError: '✕ Sending failed',
  shareSendBtn: 'Send',
  
  // Notifications
  notifications: 'Notifications',
  noNotifications: 'No notifications',
  projectSharedBy: (from: unknown) => `Project shared by ${from}`,
  importToHome: '⬇ Import to Home',
  importing: 'Importing…',
  importNotifSuccess: '✓ Imported to Home',
  importNotifError: '✕ Import failed',
  markRead: 'Mark as read',
  
  // Import from link
  importBtn: 'Import a project',
  importTitle: 'Import a project',
  importLinkLabel: 'Project link or ID',
  importLinkPh: 'Paste the link received by e-mail…',
  importSuccess: '✓ Project imported to Home',
  importError: '✕ Import failed',

  // Auth
  authLogin: 'Sign in',
  authSignup: 'Create account',
  authEmail: 'E-mail',
  authPassword: 'Password',
  authNickname: 'Nickname (optional)',
  authLoggingIn: 'Signing in…',
  authSigningUp: 'Signing up…',
  authNoAccount: 'Don\'t have an account?',
  authHasAccount: 'Already have an account?',
  authCreateAccount: 'Create account',
  authLoginLink: 'Sign in',
  authForgotPwd: 'Forgot password?',
  authServerError: 'Invalid server response',
  authBadCredentials: 'Invalid credentials',
  authVerifyTitle: 'Verify your e-mail',
  authVerifyMsg: 'A verification code was sent to',
  authVerifyCode: 'Verification code (6 digits)',
  authVerifying: 'Verifying…',
  authVerifyBtn: 'Verify',
  authResendCode: 'Resend code',
  authSignupSuccess: 'Account created! You can now sign in.',
  authSignupSuccessVerify: 'Account created! Check your e-mail to activate your account.',
  authResetTitle: 'Reset password',
  authResetMsg: 'Enter your e-mail to receive a reset link.',
  authResetSending: 'Sending…',
  authResetSend: 'Send reset link',
  authResetSent: 'If this e-mail exists, a reset link has been sent.',
  authBackToLogin: 'Back to sign in',
  authWelcome: 'Collective Intelligence',
  authWelcomeSub: 'Sign in to continue',

  // Brand tagline (free-tier floating badge)
  tagline: "Let's harness collective intelligence",

  // Upgrade CTA + modal (free tier only)
  upgradeBtn: 'Upgrade to paid plan',
  upgradeTitle: 'Upgrade to paid plan',
  upgradeSubtitle: 'Unlock collaboration, server storage, project sharing, and user accounts. Pick the plan that fits your organization.',
  upgradePopular: 'Popular',
  upgradeClose: 'Close',
  upgradeFooter: 'All plans include: C-K mindmaps, whiteboards, text documents, PDF export.',
  upgradePerUserYear: '/ year / user',
  upgradeCustomQuote: 'Custom quote',
  upgradeSeatsUnlimited: 'Unlimited seats',
  upgradeSeats1to2: '1 to 2 seats',
  upgradeSeats3to10: '3 to 10 seats',

  tierSchoolName: 'School & NGO',
  tierSchoolDesc: 'Schools, universities, non-profits. No collaborator limit.',
  tierIndepName: 'Freelancer',
  tierIndepDesc: 'Freelancers and small teams. Light collaboration.',
  tierTpeName: 'Small & Medium Business',
  tierTpeDesc: 'Organized company teams. Volume pricing.',
  tierLargeName: 'Enterprise',
  tierLargeDesc: 'SSO, dedicated deployment, priority support, SLA.',

  ctaContactUs: 'Contact us',
  ctaSubscribe: 'Subscribe',
  ctaRequestQuote: 'Request a quote',
};

/**
 * Complete translation dictionary for all languages.
 */
/**
 * Spanish translations.
 * Inherits from English for untranslated keys (spread) then overrides the
 * user-facing strings with Spanish. As the product matures, remaining
 * English strings should be translated in place here.
 */
const es: Translations = {
  ...en,
  // FolderNavigator toolbar
  newRootFolder: 'Nueva carpeta raíz',
  newFolder: 'Nueva carpeta',
  newProject: 'Nuevo proyecto',
  newWhiteboard: 'Nueva pizarra',
  newTextDoc: 'Nuevo documento de texto',
  save: 'Guardar',
  settings: 'Ajustes',

  // Tree
  home: 'Inicio',
  empty: 'Vacío',
  folderPlaceholder: 'Nombre de la carpeta',
  projectPlaceholder: 'Nombre del proyecto',
  deleteConfirm: (name: unknown) => `¿Eliminar "${name}"?`,

  // Context menu
  ctxNewFolder: '📁 Nueva carpeta',
  ctxNewProject: '💡 Nuevo proyecto',
  ctxNewWhiteboard: 'Nueva pizarra',
  ctxNewTextDoc: 'Nuevo documento de texto',
  ctxRename: '✏️ Renombrar',
  ctxDuplicate: '📋 Duplicar',
  ctxDelete: '🗑️ Eliminar',

  // Rename modal
  renameTitle: 'Renombrar',
  ok: 'OK',
  cancel: 'Cancelar',

  // User bar
  user: 'Usuario',
  logout: 'Cerrar sesión',
  about: 'Acerca de',
  aboutTitle: 'Acerca de esta herramienta',
  aboutBody: "Esta herramienta está desarrollada por ESSONE GROUP, matriz de IPFLUENCER SAS y empresa detrás del servicio CertifAfrica. El enfoque de ideación utilizado es el método C-K, desarrollado por MINES PARIS TECH. Este método estructurado de creación ha demostrado su eficacia en numerosos campos sin límite aparente.",
  aboutSlogan: 'Impactemos el mundo positivamente',

  // Import / Export
  importBtn: 'Importar',
  importTitle: 'Importar un proyecto',
  importLinkLabel: 'Enlace o identificador del proyecto',
  importLinkPh: 'Pega el enlace recibido por correo…',
  exportBtn: 'Exportar',
  exportSvg: 'SVG',
  exportSvgHint: 'Vectorial, máxima calidad',
  exportPng: 'PNG',
  exportPngHint: 'Imagen de alta resolución (×3)',
  exportPdf: 'PDF',
  exportPdfHint: 'Vía impresión del navegador',
  exportWorkbook: 'Exportar el workbook',
  exportWorkbookHint: 'Todas tus carpetas y proyectos en un .json',
  importWorkbook: 'Importar un workbook',
  importWorkbookLabel: 'Elige un archivo .json exportado previamente. Carpetas y proyectos existentes se fusionarán.',

  // Share
  shareBtn: 'Compartir',

  // Auth
  authWelcome: 'Inteligencia Colectiva',
  authWelcomeSub: 'Inicia sesión para continuar',

  // Brand tagline (free-tier floating badge)
  tagline: 'Movilicemos la inteligencia colectiva',

  // Upgrade CTA + modal (free tier only)
  upgradeBtn: 'Pasar al plan de pago',
  upgradeTitle: 'Pasar al plan de pago',
  upgradeSubtitle: 'Desbloquea la colaboración, el almacenamiento en servidor, el uso compartido de proyectos y las cuentas de usuario. Elige el plan adaptado a tu organización.',
  upgradePopular: 'Popular',
  upgradeClose: 'Cerrar',
  upgradeFooter: 'Todos los planes incluyen: mapas mentales C-K, pizarras, documentos de texto, exportación PDF.',
  upgradePerUserYear: '/ año / usuario',
  upgradeCustomQuote: 'Bajo presupuesto',
  upgradeSeatsUnlimited: 'Plazas ilimitadas',
  upgradeSeats1to2: '1 a 2 plazas',
  upgradeSeats3to10: '3 a 10 plazas',

  tierSchoolName: 'Escuelas & ONG',
  tierSchoolDesc: 'Escuelas, universidades, asociaciones. Sin límite de colaboradores.',
  tierIndepName: 'Independiente',
  tierIndepDesc: 'Freelancers y equipos pequeños. Colaboración ligera.',
  tierTpeName: 'PyME',
  tierTpeDesc: 'Equipos organizados en empresa. Tarifa decreciente.',
  tierLargeName: 'Grandes Empresas',
  tierLargeDesc: 'SSO, despliegue dedicado, soporte prioritario, SLA.',

  ctaContactUs: 'Contáctanos',
  ctaSubscribe: 'Suscribirse',
  ctaRequestQuote: 'Solicitar presupuesto',
};

export const TRANSLATIONS: TranslationDictionary = { fr, en, es };

/**
 * Gets translations for a specific language.
 * Falls back to French if the language is not found.
 */
export const getTranslations = (lang: Lang): Translations => {
  const translations = TRANSLATIONS[lang];
  return translations ?? TRANSLATIONS.fr;
};

/**
 * Creates a translate function for a specific language.
 * 
 * @example
 * const t = createTranslateFn('fr');
 * console.log(t('home')); // "Accueil"
 * console.log(t('deleteConfirm', 'Mon Projet')); // "Supprimer "Mon Projet" ?"
 */
export const createTranslateFn = (lang: Lang) => {
  const translations = getTranslations(lang);
  
  return (key: string, ...args: unknown[]): string => {
    const value = translations[key];
    if (typeof value === 'function') {
      return (value as (...args: unknown[]) => string)(...args);
    }
    return value as string;
  };
};
